import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FullCalendar,
  dayGridPlugin,
  frLocale,
  interactionPlugin,
  themePlugin,
  type CalendarRef,
  type DateSelectInfo,
  type DatesSetInfo,
  type DayCellInfo,
  type EventClickInfo,
  type EventDisplayInfo,
  type EventDropInfo,
  type EventInput,
} from '../../fullcalendar';
import { U3301ROLES, NS } from '../../U3301';
import { useU3301Context } from '../../U3301Context';
import {
  useU3301EventMoveHandlers,
  useU3301EventMutationHandlers,
  useU3301EventSearchHandlers,
  useU3301EventSearchNavigateHandlers,
} from '../../hooks/useU3301EventHandlers';
import type { U3301EventSearchNavigateNeighbor, U3301EventSearchRow } from '../../U3301Service';
import { u3301SearchNavigateCurrentIndex } from '../../U3301Service';
import {
  u3301FcDateStrToCivilYmd,
  u3301FcExclusiveEndToInclusiveYmd,
  u3301FormatAgendaPeriodTitle,
} from '../../utils/u3301AgendaDateRange';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { u2000TodayOrgYmd } from '../../../u2000Core/tools/U2000DateTimeHelpers';
import U3301AgendaToolbar from './U3301AgendaToolbar';
import U3301EventDialog from './U3301EventDialog';
import U3301CreateManualEventDialog from './U3301CreateManualEventDialog';

type CalendarView = 'dayGridMonth' | 'dayGridWeek';

const HIGHLIGHT_EVENT_CLASS = 'u3301-search-highlight';
const HIGHLIGHT_DAY_CLASS = 'u3301-search-highlight-day';
const HIGHLIGHT_DAYNUM_CLASS = 'u3301-search-highlight-daynum';

const U3301Agenda: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { initLU, agendaFilters, setAgendaFilters } = useU3301Context();
  const { searchEvents } = useU3301EventSearchHandlers();
  const { navigateSearchEvents } = useU3301EventSearchNavigateHandlers();
  const { moveEvent } = useU3301EventMoveHandlers();
  const { createEvent, updateEvent, deleteEvent } = useU3301EventMutationHandlers();

  const calendarRef = useRef<CalendarRef>(null);
  const initialDate = useMemo(() => u2000TodayOrgYmd(), []);
  const [view, setView] = useState<CalendarView>('dayGridMonth');
  const [range, setRange] = useState<{ dateFrom: string; dateTo: string } | null>(null);
  const [periodTitle, setPeriodTitle] = useState('');
  const [events, setEvents] = useState<U3301EventSearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<U3301EventSearchRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<string>(initialDate);
  const [createEndDate, setCreateEndDate] = useState<string | undefined>(undefined);

  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(null);
  const [highlightedEventDate, setHighlightedEventDate] = useState<string | null>(null);
  const [searchNavigateActive, setSearchNavigateActive] = useState(false);
  const [hasPreviousOccurrence, setHasPreviousOccurrence] = useState(false);
  const [hasNextOccurrence, setHasNextOccurrence] = useState(false);
  const [searchOccurrenceIndex, setSearchOccurrenceIndex] = useState(0);
  const [searchOccurrenceTotal, setSearchOccurrenceTotal] = useState(0);
  const [previousNeighbor, setPreviousNeighbor] = useState<U3301EventSearchNavigateNeighbor | null>(null);
  const [nextNeighbor, setNextNeighbor] = useState<U3301EventSearchNavigateNeighbor | null>(null);

  const canEdit = hasRole(U3301ROLES.EDIT);
  const canAdd = hasRole(U3301ROLES.ADD);
  const canDelete = hasRole(U3301ROLES.DELETE);
  const localeCode = (i18n.resolvedLanguage ?? i18n.language).startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    setLogicalUnitBreadcrumb([]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [setLogicalUnitBreadcrumb]);

  const clearSearchNavigate = useCallback(() => {
    setHighlightedEventId(null);
    setHighlightedEventDate(null);
    setSearchNavigateActive(false);
    setHasPreviousOccurrence(false);
    setHasNextOccurrence(false);
    setSearchOccurrenceIndex(0);
    setSearchOccurrenceTotal(0);
    setPreviousNeighbor(null);
    setNextNeighbor(null);
  }, []);

  const applyNavigateResult = useCallback(
    (result: NonNullable<Awaited<ReturnType<typeof navigateSearchEvents>>>) => {
      if (!result.current) {
        clearSearchNavigate();
        toast.info(t('msgSearchNoMatch', { ns: NS }), t('lblSearchText', { ns: NS }));
        return;
      }

      setHighlightedEventId(result.current.id);
      setHighlightedEventDate(result.current.eventDate);
      setSearchNavigateActive(true);
      setHasPreviousOccurrence(result.hasPrevious);
      setHasNextOccurrence(result.hasNext);
      setSearchOccurrenceIndex(u3301SearchNavigateCurrentIndex(result.previousCount, true));
      setSearchOccurrenceTotal(result.totalMatches);
      setPreviousNeighbor(result.previous);
      setNextNeighbor(result.next);
      calendarRef.current?.getApi().gotoDate(result.current.eventDate);
    },
    [clearSearchNavigate, t, toast],
  );

  const runNavigate = useCallback(
    async (opts?: { searchText?: string; currentEventId?: string }) => {
      const searchText = (opts?.searchText ?? agendaFilters.searchText).trim();
      if (searchText === '') {
        clearSearchNavigate();
        return;
      }
      const result = await navigateSearchEvents({
        searchText,
        categoryIds: agendaFilters.categoryIds.length > 0 ? agendaFilters.categoryIds : undefined,
        currentEventId: opts?.currentEventId,
      });
      if (!result) return;
      applyNavigateResult(result);
    },
    [
      agendaFilters.categoryIds,
      agendaFilters.searchText,
      applyNavigateResult,
      clearSearchNavigate,
      navigateSearchEvents,
    ],
  );

  const reloadEvents = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    const rows = await searchEvents({
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      categoryIds: agendaFilters.categoryIds.length > 0 ? agendaFilters.categoryIds : undefined,
      searchText: agendaFilters.searchText.trim() || undefined,
    });
    setEvents(rows);
    setLoading(false);
  }, [agendaFilters.categoryIds, agendaFilters.searchText, range, searchEvents]);

  useEffect(() => {
    void reloadEvents();
  }, [reloadEvents]);

  const categoryIdsKey = agendaFilters.categoryIds.join(',');
  const categoryNavigateRef = useRef(categoryIdsKey);
  useEffect(() => {
    if (categoryNavigateRef.current === categoryIdsKey) return;
    categoryNavigateRef.current = categoryIdsKey;
    if (!searchNavigateActive || !highlightedEventId) return;
    void runNavigate({ currentEventId: highlightedEventId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-nav uniquement quand les catégories changent
  }, [categoryIdsKey]);

  const fcEvents: EventInput[] = useMemo(
    () =>
      events.map((e) => {
        const isHighlighted = e.id === highlightedEventId;
        return {
          id: e.id,
          title: e.title,
          start: e.eventDate,
          allDay: true,
          color: isHighlighted ? '#F59E0B' : e.category.effectiveColorCode,
          contrastColor: isHighlighted ? '#1C1917' : undefined,
          editable: canEdit && e.category.editableInCalendar,
          className: isHighlighted ? HIGHLIGHT_EVENT_CLASS : undefined,
          extendedProps: { row: e, categoryColor: e.category.effectiveColorCode },
        };
      }),
    [canEdit, events, highlightedEventId],
  );

  const handleDatesSet = (arg: DatesSetInfo) => {
    const dateFrom = u3301FcDateStrToCivilYmd(arg.startStr);
    const dateTo = u3301FcExclusiveEndToInclusiveYmd(arg.endStr);
    if (!dateFrom || !dateTo) return;

    const cs = arg.view.currentStart;
    const currentStartYmd = `${cs.getFullYear()}-${String(cs.getMonth() + 1).padStart(2, '0')}-${String(cs.getDate()).padStart(2, '0')}`;

    setPeriodTitle(
      u3301FormatAgendaPeriodTitle(arg.view.type, currentStartYmd, dateFrom, dateTo, localeCode),
    );

    setRange((prev) => {
      if (prev?.dateFrom === dateFrom && prev?.dateTo === dateTo) return prev;
      return { dateFrom, dateTo };
    });
  };

  const handleEventDrop = async (arg: EventDropInfo) => {
    const row = arg.event.extendedProps.row as U3301EventSearchRow | undefined;
    const newDate = u3301FcDateStrToCivilYmd(arg.event.startStr);
    if (!row || !newDate || !canEdit) {
      arg.revert();
      return;
    }
    if (!row.category.editableInCalendar) {
      arg.revert();
      return;
    }
    try {
      await moveEvent(row.id, { eventDate: newDate, version: row.version });
      await reloadEvents();
    } catch {
      arg.revert();
    }
  };

  const handleEventClick = (arg: EventClickInfo) => {
    const row = arg.event.extendedProps.row as U3301EventSearchRow | undefined;
    if (row) setSelectedEvent(row);
  };

  const handleDateSelect = (arg: DateSelectInfo) => {
    if (!canAdd) return;
    const ymd = u3301FcDateStrToCivilYmd(arg.startStr);
    if (!ymd) return;
    const endInclusive = u3301FcExclusiveEndToInclusiveYmd(arg.endStr);
    setCreateDate(ymd);
    setCreateEndDate(endInclusive && endInclusive !== ymd ? endInclusive : undefined);
    setCreateOpen(true);
    arg.view.calendar.unselect();
  };

  const handleSearchTextChange = useCallback((searchText: string) => {
    setAgendaFilters((f) => (f.searchText === searchText ? f : { ...f, searchText }));
  }, [setAgendaFilters]);

  const handleCategoryIdsChange = useCallback((categoryIds: string[]) => {
    setAgendaFilters((f) => ({ ...f, categoryIds }));
  }, [setAgendaFilters]);

  const handleSearchSubmit = useCallback(
    (searchText: string) => {
      void runNavigate({ searchText });
    },
    [runNavigate],
  );

  const handleSearchPrevOccurrence = useCallback(() => {
    if (!previousNeighbor) return;
    void runNavigate({ currentEventId: previousNeighbor.id });
  }, [previousNeighbor, runNavigate]);

  const handleSearchNextOccurrence = useCallback(() => {
    if (!nextNeighbor) return;
    void runNavigate({ currentEventId: nextNeighbor.id });
  }, [nextNeighbor, runNavigate]);

  const manualCategories = initLU.categories.filter((c) => c.origin === 'MANUAL' && c.editableInCalendar && c.active);

  const locale = localeCode === 'fr' ? frLocale : 'en';

  const isHighlightedDay = useCallback(
    (arg: DayCellInfo) => {
      if (!highlightedEventDate) return false;
      const d = arg.date;
      const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return ymd === highlightedEventDate;
    },
    [highlightedEventDate],
  );

  const dayCellClass = useCallback(
    (arg: DayCellInfo) => (isHighlightedDay(arg) ? HIGHLIGHT_DAY_CLASS : ''),
    [isHighlightedDay],
  );

  const dayCellTopClass = useCallback(
    (arg: DayCellInfo) => (isHighlightedDay(arg) ? HIGHLIGHT_DAYNUM_CLASS : ''),
    [isHighlightedDay],
  );

  const renderEventContent = useCallback((arg: EventDisplayInfo) => {
    const isHighlighted = arg.event.id === highlightedEventId;
    return (
      <div className="flex min-w-0 items-center overflow-hidden px-0.5">
        {isHighlighted && (
          <span className="u3301-search-highlight-marker" aria-hidden>
            ★
          </span>
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="truncate">{arg.event.title}</div>
        </div>
      </div>
    );
  }, [highlightedEventId]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-3">
      <style>{`
        @keyframes u3301-search-pulse {
          0%, 100% { box-shadow: 0 0 0 3px #92400E, 0 0 0 6px rgba(245, 158, 11, 0.55); }
          50% { box-shadow: 0 0 0 4px #78350F, 0 0 0 10px rgba(245, 158, 11, 0.25); }
        }
        .${HIGHLIGHT_DAY_CLASS} {
          background-color: rgba(245, 158, 11, 0.18) !important;
        }
        .${HIGHLIGHT_DAYNUM_CLASS} {
          font-weight: 700 !important;
          color: #92400E !important;
          background: #FDE68A !important;
          border-radius: 9999px !important;
          min-width: 1.75rem !important;
          text-align: center !important;
        }
        .${HIGHLIGHT_EVENT_CLASS} {
          z-index: 8 !important;
          font-weight: 700 !important;
          border-width: 3px !important;
          border-style: solid !important;
          border-color: #78350F !important;
          animation: u3301-search-pulse 1.4s ease-in-out 3;
        }
        .${HIGHLIGHT_EVENT_CLASS} .u3301-search-highlight-marker {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 1.1rem;
          height: 1.1rem;
          margin-right: 0.25rem;
          border-radius: 9999px;
          background: #78350F;
          color: #FEF3C7;
          font-size: 0.65rem;
          line-height: 1;
        }
      `}</style>
      <U3301AgendaToolbar
        view={view}
        onViewChange={(v) => {
          setView(v);
          calendarRef.current?.getApi().changeView(v);
        }}
        onToday={() => calendarRef.current?.getApi().today()}
        onPrev={() => calendarRef.current?.getApi().prev()}
        onNext={() => calendarRef.current?.getApi().next()}
        periodTitle={periodTitle}
        searchText={agendaFilters.searchText}
        onSearchTextChange={handleSearchTextChange}
        onSearchSubmit={handleSearchSubmit}
        onSearchNavigateClear={clearSearchNavigate}
        onSearchPrevOccurrence={handleSearchPrevOccurrence}
        onSearchNextOccurrence={handleSearchNextOccurrence}
        searchNavigateActive={searchNavigateActive}
        hasPreviousOccurrence={hasPreviousOccurrence}
        hasNextOccurrence={hasNextOccurrence}
        searchOccurrenceIndex={searchOccurrenceIndex}
        searchOccurrenceTotal={searchOccurrenceTotal}
        categoryIds={agendaFilters.categoryIds}
        onCategoryIdsChange={handleCategoryIdsChange}
        categories={initLU.categories}
        onCreateClick={
          canAdd
            ? () => {
                setCreateDate(u2000TodayOrgYmd());
                setCreateEndDate(undefined);
                setCreateOpen(true);
              }
            : undefined
        }
      />

      {loading && <div className="text-sm text-gray-500">{t('msgLoading', { ns: 'common', defaultValue: '…' })}</div>}

      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white p-2">
        <FullCalendar
          ref={calendarRef}
          plugins={[themePlugin, dayGridPlugin, interactionPlugin]}
          initialView={view}
          initialDate={initialDate}
          headerToolbar={false}
          locale={locale}
          firstDay={0}
          height="100%"
          editable={canEdit}
          selectable={canAdd}
          selectMirror
          events={fcEvents}
          dayCellClass={dayCellClass}
          dayCellTopClass={dayCellTopClass}
          eventContent={renderEventContent}
          datesSet={handleDatesSet}
          eventDrop={handleEventDrop}
          eventClick={handleEventClick}
          select={handleDateSelect}
          dayMaxEvents
        />
      </div>

      {selectedEvent && (
        <U3301EventDialog
          event={selectedEvent}
          canEdit={canEdit}
          canDelete={canDelete}
          onClose={() => setSelectedEvent(null)}
          onSaveManual={async (payload) => {
            const result = await updateEvent({
              id: selectedEvent.id,
              version: selectedEvent.version,
              ...payload,
            });
            if (result) {
              setSelectedEvent(null);
              await reloadEvents();
            }
          }}
          onMove={async (eventDate) => {
            try {
              await moveEvent(selectedEvent.id, { eventDate, version: selectedEvent.version });
              setSelectedEvent(null);
              await reloadEvents();
            } catch {
              /* toast déjà affiché */
            }
          }}
          onDelete={async (scope) => {
            const ok = await deleteEvent({
              id: selectedEvent.id,
              version: selectedEvent.version,
              scope,
            });
            if (ok) {
              setSelectedEvent(null);
              await reloadEvents();
            }
          }}
        />
      )}

      {createOpen && (
        <U3301CreateManualEventDialog
          initialDate={createDate}
          initialEndDate={createEndDate}
          categories={manualCategories}
          onClose={() => setCreateOpen(false)}
          onSave={async (payload) => {
            const results = await createEvent(payload);
            if (results) {
              setCreateOpen(false);
              await reloadEvents();
            }
          }}
        />
      )}
    </div>
  );
};

export default U3301Agenda;
